import { Injectable } from '@angular/core';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { isNil as _isNil } from 'lodash';
import { of as ObservableOf } from 'rxjs/observable/of';

import { BlConditionType } from '@blcloud/bl-ng-common';
import {
  CustomerInfoResource,
  OperationHistoryLogResource,
  ReceptionAnalysisItemSettingResource,
  ReceptionInfoResource
} from '@blcloud/bl-ng-resource';
import {
  IReceptionAnalysisItemSetting,
  IReceptionInfo,
  IBizNegoInfo,
  ICustomerInfo,
  IOperationHistoryLog,
  ISfOperationHistoryLog,
  IRepairOperationHistoryLog,
  IVehicleInfo,
} from '@blcloud/bl-datamodel';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { ReceptionAnalysisDivCode } from '@blcloud/bl-datamodel/enum/deals/reception-analysis-div-code';
import { ReceptionAnalysisItemDisplayDiv } from '@blcloud/bl-datamodel/enum/deals/reception-analysis-item-display-div';
import { BizNegoStatus, BizNegoStatusMap } from '@blcloud/bl-datamodel/enum/deals/biz-nego-status';

import { ICancelReason, IReceptStateModalEditData } from './recept-state-modal.interface';
import { ProductCode } from '@blcloud/bl-datamodel/enum/common/product-code';
import { StringUtils } from '@blcloud/bl-common';
import { OpeHistoryKindDiv } from '@blcloud/bl-datamodel/enum/bizcmn/ope-history-kind-div';
import { PAGE_ID, PAGE_TITLE } from '../../recept-list-page.define';
import { OperationContentDiv } from '@blcloud/bl-datamodel/enum/bizcmn/operation-content-div';

/**
 * 受付状況モーダルサービス
 */
@Injectable()
export class ReceptStateModalService {
  /** コンストラクタ */
  constructor(
    private receptionAnalysisItemSettingResource: ReceptionAnalysisItemSettingResource,
    private receptionInfoResource: ReceptionInfoResource,
    private customerInfoResource: CustomerInfoResource,
    private operationHistoryLogResource: OperationHistoryLogResource
  ) {}

  /**
   * キャンセル理由一覧
   */
  getCancelReasonList(organizationCode: string): Observable<ICancelReason[]> {
    const conditions = [
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
      { key: 'reserveAnalysisDivCode', value: ReceptionAnalysisDivCode.CancelReason, type: BlConditionType.Equal },
      { key: 'organizationCode', value: organizationCode, type: BlConditionType.Equal },
    ];

    const params = {
      conditions: conditions,
      sortFields: [
        { field: 'reserveAnalysisDivCode', desc: false },
        { field: 'positiveInteger', desc: false },
      ],
    };
    return this.receptionAnalysisItemSettingResource.getAll<IReceptionAnalysisItemSetting>(params).pipe(
      map((response) =>
        (response ? response.searchResultList : []).map((v) => ({
          code: v.reserveAnalysisItemCode,
          name: v.reserveAnalysisItemName,
          isDefault: v.reserveAnalysisItemDisplayDiv === ReceptionAnalysisItemDisplayDiv.Display,
        }))
      ),
      map((cancelReasonList) =>
        cancelReasonList.length > 0
          ? [
              { code: -1 }, // NOTE: -1は未選択状態にする為に追加
              ...cancelReasonList,
            ]
          : cancelReasonList
      )
    );
  }

  /**
   * 受付情報を取得する
   */
  getReceptionInfo(receptionInfo: IReceptionInfo): Observable<IReceptionInfo> {
    const id = this.receptionInfoResource.getIdString(receptionInfo);
    return this.receptionInfoResource.getById(id);
  }

  /**
   * 受付情報を更新する
   */
  putReceptionInfo(
    receptionInfo: IReceptionInfo,
    bizNegoInfo: IBizNegoInfo,
    updateData: IReceptStateModalEditData,
    vehicleInfo?: IVehicleInfo
  ): Observable<IReceptionInfo[]> {
    // 受付再開或は完了取消場合
    const newState = this.determineNewStatus(bizNegoInfo, updateData);

    // 受付情報は取り直す
    return this.getReceptionInfo(receptionInfo).pipe(
      mergeMap((response) => {
        const receptionInfoList: IReceptionInfo[] = [response];
        return this.receptionInfoResource.put<IReceptionInfo[]>(
          {
            bizNegoStatus: newState,
            bizNegoStatusEnumName: BizNegoStatusMap[newState],
            cancelDate: updateData.cancelDate,
            cancelReason: updateData.cancelReason,
            cancelReasonAnalysisItemCode: updateData.cancelReasonAnalysisItemCode === -1 ? null : updateData.cancelReasonAnalysisItemCode,
            receptionInfoList,
          },
          {
            entityOptions: {
              exPath: 'confirmedmultiplchgstatus',
            },
          }
        );
      }),
      tap((response) => {
        this.registOperationHistoryLog(response[0], vehicleInfo)
          .pipe(
            catchError(() => {
              return ObservableOf(response);
            })
          )
          .subscribe();
      })
    );
  }

  /**
   * 操作履歴ログ情報を登録する
   * @param reception 受付情報
   */
  private registOperationHistoryLog(reception: IReceptionInfo, vehicleInfo?: IVehicleInfo): Observable<any> {
    switch (reception.productCode) {
      case ProductCode.SuperFrontman:
        break;
      case ProductCode.Repair:
        break;
      default:
        return ObservableOf(null);
    }
    return this.getCustomerInfoByCode(reception.customerCode).pipe(
      mergeMap((customerInfo) => {
        const bizNego = reception.bizNegoInfoList[0];
        const opeHistoryMessage = StringUtils.concat('受付状況の変更を行いました。（受付状況：', bizNego.bizNegoStatusEnumName, '）');
        const customerName = StringUtils.concatWith(
          '　',
          customerInfo && customerInfo.customerName1,
          customerInfo && customerInfo.customerName2
        );
        const vehicleModelFixFullWName =
          vehicleInfo &&
          vehicleInfo.vehicleSpecificationInfo &&
          vehicleInfo.vehicleSpecificationInfo.vehicleModelFixFullWName;
        const operationHistoryLog: IOperationHistoryLog = {
          productCode: ProductCode.Common,
          opeHistoryKindDiv: OpeHistoryKindDiv.Record,
          pageId: PAGE_ID,
          opeHistoryFunctionName: PAGE_TITLE,
          operationContentDiv: OperationContentDiv.DataUpdate,
          opeHistoryMessageList: [opeHistoryMessage],
          subjects: {
            sf: null,
            repair: null,
          },
        };
        switch (reception.productCode) {
          case ProductCode.SuperFrontman:
            const sfOperationHistoryLog: ISfOperationHistoryLog = {
              receptionNum: reception.receptionNum,
              slipNumPrefix: null,
              slipNumber: null,
              operationSlipKindDiv: null,
              customerCode: reception.customerCode,
              vcleInspRegistOfficeCd: reception.vcleInspRegistOfficeCd,
              vcleInspRegistOfficeName: reception.vcleInspRegistOfficeName,
              numPlateClassNum: reception.numPlateClassNum,
              numPlateKana: reception.numPlateKana,
              numPlateSeriesSpecifNum: reception.numPlateSeriesSpecifNum,
              vehicleMgtNumber: reception.vehicleMgtNumber,
              customerName: customerName,
              vehicleModelFixFullWName: vehicleModelFixFullWName,
            };
            operationHistoryLog.subjects.sf = sfOperationHistoryLog;
            break;
          case ProductCode.Repair:
            const repairOperationHistoryLog: IRepairOperationHistoryLog = {
              receptionNum: reception.receptionNum,
              slipNumPrefix: null,
              slipNumber: null,
              operationSlipKindDiv: null,
              customerCode: reception.customerCode,
              vcleInspRegistOfficeCd: reception.vcleInspRegistOfficeCd,
              vcleInspRegistOfficeName: reception.vcleInspRegistOfficeName,
              numPlateClassNum: reception.numPlateClassNum,
              numPlateKana: reception.numPlateKana,
              numPlateSeriesSpecifNum: reception.numPlateSeriesSpecifNum,
              vehicleMgtNumber: reception.vehicleMgtNumber,
              customerName: customerName,
              vehicleModelFixFullWName: vehicleModelFixFullWName,
            };
            operationHistoryLog.subjects.repair = repairOperationHistoryLog;
            break;
          default:
            break;
        }
        return this.operationHistoryLogResource.post(operationHistoryLog);
      })
    );
  }

  /**
   * 顧客コードから顧客情報を取得
   * @param customerCode 顧客コード
   * @returns 顧客情報
   */
  private getCustomerInfoByCode(customerCode: number): Observable<ICustomerInfo> {
    if (!customerCode || customerCode === 0) {
      return ObservableOf(undefined);
    }
    return this.customerInfoResource.getById(String(customerCode)).pipe(
      catchError(() => {
        return ObservableOf(undefined);
      })
    );
  }

  /**
   * 新しい商談ステータスを決定する
   * @returns 適用する新しいステータス
   */
  private determineNewStatus(bizNegoInfo: IBizNegoInfo, updateData: IReceptStateModalEditData): string {
    if (bizNegoInfo.bizNegoStatus !== BizNegoStatus.Completion && bizNegoInfo.bizNegoStatus !== BizNegoStatus.Cancel) {
      return updateData.bizNegoStatus;
    }

    return _isNil(bizNegoInfo.bizNegoStatusList) || bizNegoInfo.bizNegoStatusList.length === 0
      ? BizNegoStatus.During // デフォルトステータス
      : bizNegoInfo.bizNegoStatusList[bizNegoInfo.bizNegoStatusList.length - 1];
  }
}



// WEBPACK FOOTER //
// ./src/app/page/recept-list-page/modal/recept-state-modal/recept-state-modal.service.ts